// src/components/RouteLayer.js
// ─────────────────────────────────────────────────────────────────────────────
//  ROTA REAL COM TRÁFEGO — Mapbox Directions API v5
//  Renderiza a rota pelas vias reais com segmentos coloridos por nível de
//  congestionamento (live traffic). Usa imperatively L.polyline para não
//  re-montar o MapContainer.
//
//  Props:
//    origin       { latitude, longitude }  — motorista selecionado
//    destination  { latitude, longitude }  — parceiro focado
//    onRouteReady ({ distancia, duracao, congestionamento, legs }) → void
//    onRouteClear () → void
//    onLoading    (bool) → void
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

// ── Token reutilizado do CentralOperacoes ─────────────────────────────────────
const MAPBOX_TOKEN =
    'pk.eyJ1IjoiYmxuYXNjaW1lbnRvMjQwMjk4IiwiYSI6ImNtZ2lnYnc1NTA5MmEycG9pM2M5M3IwcWMifQ.JcuK1f9M-yZdwCeTXDx20Q';

// ── Paleta de tráfego ─────────────────────────────────────────────────────────
const TRAFFIC = {
    low:      { color: '#10b981', glow: 'rgba(16,185,129,0.35)',  weight: 5 },
    moderate: { color: '#f59e0b', glow: 'rgba(245,158,11,0.35)',  weight: 5 },
    heavy:    { color: '#ef4444', glow: 'rgba(239,68,68,0.35)',   weight: 6 },
    severe:   { color: '#9333ea', glow: 'rgba(147,51,234,0.35)',  weight: 6 },
    unknown:  { color: '#3b82f6', glow: 'rgba(59,130,246,0.35)',  weight: 5 },
};

// ── Injeta animation CSS uma única vez ───────────────────────────────────────
const injectRouteCSS = () => {
    const id = 'route-layer-css';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = `
      /* Dash flow animado sobre a rota */
      .leaflet-pane .route-flow-dash path {
        animation: routeFlowDash 1.1s linear infinite;
      }
      @keyframes routeFlowDash {
        to { stroke-dashoffset: -30; }
      }
      /* Pulse nos marcadores de origem/destino da rota */
      .route-endpoint-pulse {
        animation: routeEndpointPulse 1.8s ease-out infinite;
      }
      @keyframes routeEndpointPulse {
        0%   { box-shadow: 0 0 0 0   rgba(59,130,246,0.55); }
        70%  { box-shadow: 0 0 0 14px rgba(59,130,246,0);  }
        100% { box-shadow: 0 0 0 0   rgba(59,130,246,0);   }
      }
    `;
    document.head.appendChild(el);
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const toLatLng = ([lng, lat]) => [lat, lng];

/**
 * Agrupa coordenadas consecutivas com o mesmo nível de congestionamento
 * em segmentos, para desenhar polilíneas coloridas por trecho.
 */
const buildSegments = (coords, congestions) => {
    const segs = [];
    if (!coords.length) return segs;

    let cur = {
        congestion: congestions[0] || 'unknown',
        points: [coords[0]],
    };

    for (let i = 0; i < coords.length - 1; i++) {
        const c = congestions[i] || 'unknown';
        if (c !== cur.congestion) {
            cur.points.push(coords[i]);
            segs.push({ ...cur });
            cur = { congestion: c, points: [coords[i]] };
        }
        cur.points.push(coords[i + 1]);
    }
    if (cur.points.length >= 2) segs.push(cur);
    return segs;
};

/**
 * Determina o nível de congestionamento dominante (por contagem de segmentos).
 * Prioriza o pior nível em caso de empate.
 */
const SEVERITY = { severe: 4, heavy: 3, moderate: 2, low: 1, unknown: 0 };
const dominantCongestion = (congestions) => {
    const counts = {};
    congestions.forEach(c => { counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts)
        .sort((a, b) => (SEVERITY[b[0]] || 0) - (SEVERITY[a[0]] || 0) || b[1] - a[1])[0]?.[0] || 'unknown';
};

// ════════════════════════════════════════════════════════════════════════════
//  COMPONENTE
// ════════════════════════════════════════════════════════════════════════════
export default function RouteLayer({
    origin,
    destination,
    onRouteReady,
    onRouteClear,
    onLoading,
}) {
    injectRouteCSS();

    const map         = useRef(null);
    const mapInstance = useMap();
    map.current       = mapInstance;

    const layersRef  = useRef([]);   // todas as camadas adicionadas ao mapa
    const abortRef   = useRef(null); // AbortController ativo

    // ── Limpa todas as camadas Leaflet ────────────────────────────────────────
    const clearLayers = useCallback(() => {
        layersRef.current.forEach(l => {
            try { map.current.removeLayer(l); } catch {}
        });
        layersRef.current = [];
    }, []);

    // ── Cleanup total ─────────────────────────────────────────────────────────
    const fullClear = useCallback(() => {
        if (abortRef.current) abortRef.current.abort();
        clearLayers();
        onRouteClear?.();
        onLoading?.(false);
    }, [clearLayers, onRouteClear, onLoading]);

    // ── Efeito principal: busca rota quando origem/destino mudam ──────────────
    useEffect(() => {
        const hasOrigin = origin?.latitude && origin?.longitude;
        const hasDest   = destination?.latitude && destination?.longitude;

        if (!hasOrigin || !hasDest) {
            fullClear();
            return;
        }

        // Cancela requisição anterior
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        clearLayers();
        onLoading?.(true);

        const run = async () => {
            const o = `${origin.longitude},${origin.latitude}`;
            const d = `${destination.longitude},${destination.latitude}`;

            const url = [
                `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${o};${d}`,
                `?access_token=${MAPBOX_TOKEN}`,
                `&geometries=geojson`,
                `&overview=full`,
                `&annotations=congestion,duration,distance`,
                `&steps=false`,
                `&language=pt-BR`,
                `&exclude=ferry`,          // sem ferry
            ].join('');

            try {
                const res  = await fetch(url, { signal: abortRef.current.signal });
                const data = await res.json();

                onLoading?.(false);

                if (!data.routes?.length) {
                    drawFallback(origin, destination);
                    onRouteReady?.({ distancia: null, duracao: null, congestionamento: 'unknown', fallback: true });
                    return;
                }

                const route       = data.routes[0];
                const { geometry, duration, distance, legs } = route;
                const coords      = geometry.coordinates;           // [lng, lat][]
                const congestions = legs[0]?.annotation?.congestion || [];
                const dominant    = dominantCongestion(congestions);

                // ── 1. Sombra da rota (casing escuro) ────────────────────────
                const allLLs   = coords.map(toLatLng);
                const shadow   = L.polyline(allLLs, {
                    color: 'rgba(0,0,0,0.22)',
                    weight: 11,
                    lineCap: 'round',
                    lineJoin: 'round',
                    interactive: false,
                    pane: 'shadowPane',
                }).addTo(map.current);
                layersRef.current.push(shadow);

                // ── 2. Segmentos coloridos por nível de tráfego ───────────────
                const segments = buildSegments(coords, congestions);
                segments.forEach(seg => {
                    const { color, weight } = TRAFFIC[seg.congestion] || TRAFFIC.unknown;
                    const lls = seg.points.map(toLatLng);
                    if (lls.length < 2) return;

                    const line = L.polyline(lls, {
                        color,
                        weight,
                        opacity: 0.92,
                        lineCap: 'round',
                        lineJoin: 'round',
                        interactive: false,
                    }).addTo(map.current);
                    layersRef.current.push(line);
                });

                // ── 3. Borda branca fina (highlight) ─────────────────────────
                const highlight = L.polyline(allLLs, {
                    color: 'rgba(255,255,255,0.40)',
                    weight: 2,
                    lineCap: 'round',
                    lineJoin: 'round',
                    interactive: false,
                }).addTo(map.current);
                layersRef.current.push(highlight);

                // ── 4. Dash animado "flow" ────────────────────────────────────
                const flow = L.polyline(allLLs, {
                    color: 'rgba(255,255,255,0.65)',
                    weight: 2.5,
                    dashArray: '12 20',
                    dashOffset: '0',
                    lineCap: 'round',
                    interactive: false,
                    className: 'route-flow-dash',
                }).addTo(map.current);
                layersRef.current.push(flow);

                // ── 5. Marcador de destino (círculo pulsante) ─────────────────
                const destColor = TRAFFIC[dominant]?.color || '#3b82f6';
                const destMarker = L.circleMarker(
                    [destination.latitude, destination.longitude],
                    {
                        radius: 10,
                        color: '#fff',
                        weight: 3,
                        fillColor: destColor,
                        fillOpacity: 1,
                        className: 'route-endpoint-pulse',
                    }
                ).addTo(map.current);
                layersRef.current.push(destMarker);

                // ── 6. Ajusta bounds para incluir toda a rota ─────────────────
                try {
                    map.current.fitBounds(
                        L.latLngBounds(allLLs),
                        { padding: [80, 80], maxZoom: 15, animate: true, duration: 1.2 }
                    );
                } catch {}

                // ── 7. Retorna dados para o card de rota ──────────────────────
                onRouteReady?.({
                    distancia:        (distance / 1000).toFixed(1),
                    duracao:          Math.round(duration / 60),
                    congestionamento: dominant,
                    legs,
                });

            } catch (err) {
                if (err.name === 'AbortError') return;
                console.warn('[RouteLayer] Erro Mapbox Directions:', err.message);
                onLoading?.(false);
                drawFallback(origin, destination);
                onRouteReady?.({ distancia: null, duracao: null, congestionamento: 'unknown', fallback: true });
            }
        };

        run();

        return () => { abortRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin?.latitude, origin?.longitude, destination?.latitude, destination?.longitude]);

    // ── Cleanup no unmount ────────────────────────────────────────────────────
    useEffect(() => () => clearLayers(), [clearLayers]);

    return null;

    // ── Fallback: linha reta estilizada quando API falhar ─────────────────────
    function drawFallback(o, d) {
        clearLayers();
        const lls = [
            [o.latitude, o.longitude],
            [d.latitude, d.longitude],
        ];
        const line = L.polyline(lls, {
            color: '#3b82f6',
            weight: 4,
            opacity: 0.55,
            dashArray: '10 14',
            lineCap: 'round',
            interactive: false,
        }).addTo(map.current);
        layersRef.current.push(line);
    }
}